import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { useTheme, HEADING_FONTS, BODY_FONTS } from "../lib/theme";
import { toast } from "sonner";
import { Lock, ArrowsClockwise, Palette, TextAa, TextT, EnvelopeSimple, SignOut } from "@phosphor-icons/react";
import CMSPanel from "../components/CMSPanel";
import GuidePanel from "./admin/GuidePanel";
import FilesPanel from "./admin/FilesPanel";
import AnalyticsPanel from "./admin/AnalyticsPanel";
import DigestPanel from "./admin/DigestPanel";
import ContactEmailEditor from "./admin/ContactEmailEditor";
import FooterProductsEditor from "./admin/FooterProductsEditor";
import PolicyDocsEditor from "./admin/PolicyDocsEditor";
import KoshSettingsEditor from "./admin/KoshSettingsEditor";
import ApplicationsPanel from "./admin/ApplicationsPanel";
import CCTVPanel from "./admin/CCTVPanel";
import DeployPanel from "./admin/DeployPanel";
import ThemeSnapshotEditor from "./admin/ThemeSnapshotEditor";
import UserAccessPanel from "./admin/UserAccessPanel";

const BG_PRESETS = [
  { label: "Obsidian (default)", value: "#050505" },
  { label: "Soft Cyan",          value: "#E8F7FA" },
  { label: "Paper White",        value: "#F7F6F2" },
  { label: "Warm Bone",          value: "#F2EBE2" },
  { label: "Slate Mist",         value: "#E6ECEF" },
  { label: "Deep Navy",          value: "#0A1426" },
];

const TEXT_PRESETS_DARK_BG  = ["#F5F5F5", "#FFFFFF", "#D7D7D9", "#DFFF00", "#FF8A65"];
const TEXT_PRESETS_LIGHT_BG = ["#0A0A0A", "#1F1F1F", "#3B3B3B", "#0A1426", "#5A2E00"];

const ACCENT_PRESETS = ["#FF4500", "#FF6B35", "#DFFF00", "#00E5FF", "#7C3AED", "#10B981", "#F59E0B"];

const TABS = [
  "Guide", "Inbox", "Applications", "Reels", "Live Vision", "Case studies", "Capabilities / Products",
  "Systems layers", "Playbooks", "Knowledge", "Careers", "Files", "Analytics", "Deploy", "Site appearance",
  "User access",
];

// Maps a tab label to its RBAC key (must match backend deps.TAB_KEYS).
const TAB_KEY = {
  "Guide": "guide", "Inbox": "inbox", "Applications": "applications", "Reels": "reels",
  "Live Vision": "live_vision", "Case studies": "case_studies", "Capabilities / Products": "capabilities",
  "Systems layers": "systems", "Playbooks": "playbooks", "Knowledge": "knowledge", "Careers": "careers",
  "Files": "files", "Analytics": "analytics", "Deploy": "deploy", "Site appearance": "appearance",
  "User access": "users",
};

// ---- CMS configs ----
const REEL_CONFIG = {
  publicPath: "/reels", adminPath: "/admin/reels", itemLabel: "Reel",
  blank: { title: "", category: "Computer Vision", duration_sec: 15, what_it_solves: "", cover_image: "", video_url: "", tech: [], seo_description: "" },
  columns: [
    { key: "title", label: "Title", span: 4 },
    { key: "category", label: "Category", span: 3, mono: true },
    { key: "duration_sec", label: "Sec", span: 1, mono: true },
    { key: "what_it_solves", label: "What it solves", span: 2 },
  ],
  fields: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "category", label: "Category", type: "select", required: true, options: ["Computer Vision", "LLM Copilot", "Voice Interfaces", "Dashboards", "Optimization", "Automation"] },
    { key: "duration_sec", label: "Duration (seconds)", type: "number", placeholder: "15" },
    { key: "what_it_solves", label: "What it solves", type: "textarea", rows: 3, required: true },
    { key: "video_url", label: "Video", type: "url", upload: "stream", required: true, help: "Uploads directly to Cloudflare Stream. The thumbnail is generated automatically from the video — you don't need to upload a separate image. Wait 10–60s for encoding, then save." },
    { key: "cover_image", label: "Cover image (advanced — leave blank for auto-thumbnail)", type: "url", upload: "image", help: "Optional. Leave empty to use the frame Cloudflare auto-picks. Only fill this if you want a custom-designed poster." },
    { key: "tech", label: "Tech tags (one per line)", type: "lines", rows: 4 },
    { key: "seo_description", label: "SEO description (optional)", type: "textarea", rows: 2, help: "150-char blurb shown when this reel is shared on LinkedIn, Slack, WhatsApp, etc. Leave blank to auto-use 'What it solves'." },
  ],
};

const CASE_CONFIG = {
  publicPath: "/case-studies", adminPath: "/admin/case-studies", itemLabel: "Case study",
  blank: { title: "", industry: "", problem: "", solution: "", outcome: "", tech: [], impact_metrics: [], cover_image: "", kosh_involvement: false },
  columns: [
    { key: "title", label: "Title", span: 4 },
    { key: "industry", label: "Industry", span: 3, mono: true },
    { key: "outcome", label: "Outcome", span: 3 },
  ],
  fields: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "industry", label: "Industry", type: "text", required: true },
    { key: "problem", label: "Problem", type: "textarea", rows: 3, required: true },
    { key: "solution", label: "Solution", type: "textarea", rows: 3, required: true },
    { key: "outcome", label: "Outcome", type: "textarea", rows: 2, required: true },
    { key: "tech", label: "Tech tags (one per line)", type: "lines", rows: 4 },
    { key: "cover_image", label: "Cover image", type: "url", upload: "image" },
    { key: "kosh_involvement", label: "KOSH-featured deployment", type: "boolean", help: "Turn on when KOSH copilot was central to this deployment — it'll surface on the /kosh 'KOSH in the wild' strip." },
    { key: "testimonial_quote", label: "Customer testimonial (blockquote)", type: "textarea", rows: 3, help: "One sentence from the customer. Rendered as an oversized pull-quote on the /kosh page and on the case study detail." },
    { key: "testimonial_author", label: "Testimonial author (Name, Title at Company)", type: "text" },
    { key: "seo_description", label: "SEO description (optional)", type: "textarea", rows: 2, help: "150-char meta description for the /case-studies/:slug detail page. Leave blank to auto-use the outcome." },
    { key: "slug", label: "URL slug (auto if empty)", type: "text", help: "Auto-generated from title if blank. Change ONLY if you understand the SEO impact." },
  ],
};

const ATLAS_CONFIG = {
  publicPath: "/atlas", adminPath: "/admin/atlas", itemLabel: "Capability / Product",
  blank: { title: "", icon: "Stack", color: "#FF4500", bullets: [], order: 0, short: "", detail: "", span: "" },
  aiCopy: {
    endpoint: "/ai/atlas-copy",
    titleField: "title",
    bulletsField: "bullets",
    intoFields: ["short", "detail"],
  },
  columns: [
    { key: "title", label: "Title", span: 4 },
    { key: "icon", label: "Icon", span: 2, mono: true },
    { key: "color", label: "Color", span: 2, mono: true },
    { key: "order", label: "Order", span: 2, mono: true },
  ],
  fields: [
    { key: "title",  label: "Title (shown on homepage tile + /solutions)", type: "text", required: true },
    { key: "icon",   label: "Phosphor icon name", type: "select", options: ["Camera","ChatCircleText","Microphone","ChartLineUp","Stack","FunnelSimple","GitBranch","GearSix","Cpu","Eye","Robot","Lightning"] },
    { key: "color",  label: "Accent color", type: "color" },
    { key: "short",  label: "Homepage tile — short headline (1 sentence)", type: "text",
      placeholder: "Multi-camera defect, count, identity & posture detection at line speed.",
      help: "Shown on the small tile on the homepage 'What we orchestrate' grid." },
    { key: "detail", label: "Homepage tile — expanded detail (1-2 sentences)", type: "textarea", rows: 3,
      placeholder: "Edge-deployed pipelines (YOLO, classifiers, OCR) with hot retraining, MES integration, and operator HUDs.",
      help: "Shown when a visitor taps the homepage tile." },
    { key: "span",   label: "Tile size (advanced)", type: "select",
      options: ["", "md:col-span-2", "md:col-span-2 md:row-span-2", "md:row-span-2"],
      help: "Optional. Controls how big the tile is on the homepage grid. Leave blank for default." },
    { key: "bullets", label: "/solutions page bullets (one per line)", type: "lines", rows: 5,
      help: "Used on the /solutions detail page only. The homepage uses 'short' + 'detail' above." },
    { key: "order",  label: "Display order", type: "number" },
  ],
};

const LAYERS_CONFIG = {
  publicPath: "/system-layers", adminPath: "/admin/system-layers", itemLabel: "System layer",
  blank: { layer_id: "", name: "", description: "", tech: [], order: 0 },
  columns: [
    { key: "layer_id", label: "L#", span: 1, mono: true },
    { key: "name", label: "Name", span: 4 },
    { key: "description", label: "Description", span: 5 },
  ],
  fields: [
    { key: "layer_id", label: "Layer ID (e.g. 01, 02)", type: "text", required: true },
    { key: "name", label: "Name", type: "text", required: true },
    { key: "description", label: "Description", type: "textarea", rows: 3, required: true },
    { key: "tech", label: "Tech tags (one per line)", type: "lines", rows: 5 },
    { key: "order", label: "Display order", type: "number" },
  ],
};

const PLAYBOOKS_CONFIG = {
  publicPath: "/playbooks", adminPath: "/admin/playbooks", itemLabel: "Playbook",
  blank: { title: "", overline: "", challenges: [], workflows: [], you_build: [], outcomes: [], order: 0 },
  columns: [
    { key: "title", label: "Title", span: 3 },
    { key: "overline", label: "Overline", span: 5 },
    { key: "order", label: "Order", span: 2, mono: true },
  ],
  fields: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "overline", label: "Overline (short subtitle)", type: "text", required: true },
    { key: "challenges", label: "Challenges (one per line)", type: "lines", rows: 4 },
    { key: "workflows",  label: "Typical workflows (one per line)", type: "lines", rows: 4 },
    { key: "you_build",  label: "What you build (one per line)", type: "lines", rows: 4 },
    { key: "outcomes",   label: "Example outcomes (one per line)", type: "lines", rows: 4 },
    { key: "order", label: "Display order", type: "number" },
  ],
};

const POSITION_CONFIG = {
  publicPath: "/positions", adminPath: "/admin/positions", itemLabel: "Position",
  listAllPath: "/positions?published_only=false",   // admin sees unpublished + archived roles
  archivable: true,                                  // enables the Archive / re-enable action
  blank: { title: "", department: "Engineering", location: "", employment_type: "Full-time", seniority: "Mid", summary: "", description: "", apply_url: "https://hr.wisewit.ai/jobs/", ats_external_id: "", published: true, order: 0, custom_questions: [] },
  columns: [
    { key: "title", label: "Title", span: 4 },
    { key: "department", label: "Dept", span: 2, mono: true },
    { key: "seniority", label: "Level", span: 2, mono: true },
    { key: "location", label: "Location", span: 2 },
  ],
  fields: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "department", label: "Department", type: "select", required: true, options: ["Engineering", "Solutions", "Go-to-market", "Operations", "People"] },
    { key: "location", label: "Location", type: "text", required: true, placeholder: "Bengaluru / Remote-IN" },
    { key: "employment_type", label: "Employment type", type: "select", options: ["Full-time", "Contract", "Internship", "Part-time"] },
    { key: "seniority", label: "Seniority", type: "select", options: ["Junior", "Mid", "Senior", "Lead"] },
    { key: "summary", label: "Summary (1 sentence)", type: "textarea", rows: 2, required: true },
    { key: "description", label: "Description (Markdown)", type: "textarea", rows: 10, required: true },
    { key: "apply_url", label: "ATS deep-link (optional)", type: "url", placeholder: "https://hr.wisewit.ai/jobs/<slug>", help: "Legacy — kept for backwards compat. Candidates now apply in-app via our own form; this URL is no longer surfaced by default." },
    { key: "ats_external_id", label: "ATS reference ID", type: "text", placeholder: "WW-ENG-001" },
    { key: "custom_questions", label: "Custom application questions (one per line, max 3)", type: "lines", rows: 4, help: "Recruiter-specific short-answer questions shown to the candidate during Apply. E.g. 'Describe your largest MLOps deployment' or 'Are you open to relocating to Bengaluru?'" },
    { key: "order", label: "Display order", type: "number" },
  ],
};


const ARTICLE_CONFIG = {
  publicPath: "/articles", adminPath: "/admin/articles", itemLabel: "Article",
  blank: { title: "", slug: "", category: "", summary: "", body: "", cover_image: "", read_minutes: 4, published: true, order: 0, external_url: "", source: "" },
  columns: [
    { key: "title", label: "Title", span: 4 },
    { key: "category", label: "Category", span: 3, mono: true },
    { key: "source", label: "Source", span: 2, mono: true },
    { key: "slug", label: "Slug", span: 2, mono: true },
  ],
  fields: [
    { key: "title",        label: "Title", type: "text", required: true },
    { key: "slug",         label: "URL slug (leave blank to auto-derive from title)", type: "text", placeholder: "auto", help: "Optional. If empty we'll build one from the title — 'Great AI Post!' → 'great-ai-post'. Only fill this if you want a custom URL." },
    { key: "category",     label: "Category", type: "text", required: true, placeholder: "Computer Vision · Supply Chain Optimisation · Regulatory Compliance · …" },
    { key: "summary",      label: "Summary (1–2 sentences)", type: "textarea", rows: 2, required: true },
    { key: "external_url", label: "External link (optional)", type: "url", placeholder: "https://www.linkedin.com/posts/wisewit-solutions_…", help: "If set, the card opens this URL in a new tab instead of /knowledge/<slug>. Use for LinkedIn posts, YouTube videos, etc." },
    { key: "source",       label: "Source label", type: "select", options: ["", "LinkedIn", "Medium", "YouTube", "Substack", "External"], help: "Shown as a badge on the card when external link is set." },
    { key: "body",         label: "Body (Markdown — optional when external link is set)", type: "textarea", rows: 12 },
    { key: "cover_image",  label: "Cover image", type: "url", upload: "image" },
    { key: "read_minutes", label: "Read time (minutes)", type: "number" },
    { key: "order",        label: "Display order", type: "number" },
  ],
};

export default function Admin() {
  const { roleSlug } = useParams();
  const [token, setToken] = useState(() => localStorage.getItem("ww_admin_token") || "");
  const [principal, setPrincipal] = useState(null);   // {is_super, tabs, email, name, role_slug}
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState({ submissions: [], newsletter: [], knowledge_leads: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("Guide");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const { settings, save } = useTheme();
  const [savingTheme, setSavingTheme] = useState(false);

  const hasTab = (label) => {
    if (!principal) return false;
    if (principal.is_super || (principal.tabs || []).includes("*")) return true;
    return (principal.tabs || []).includes(TAB_KEY[label]);
  };
  const visibleTabs = TABS.filter(hasTab);

  const loadInbox = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Token": t } });
      setData(data);
    } catch { /* HR-type roles can't see inbox — ignore */ }
  };

  // Authenticate via master token OR JWT, then resolve the principal + tabs.
  const authenticate = async (cred) => {
    setLoading(true);
    setLoginErr("");
    try {
      const { data: who } = await axios.get(`${API}/admin/whoami`, { headers: { "X-Admin-Token": cred } });
      setPrincipal(who);
      setToken(cred);
      localStorage.setItem("ww_admin_token", cred);
      setLoaded(true);
      const firstTab = TABS.find((t) => who.is_super || (who.tabs || []).includes("*") || (who.tabs || []).includes(TAB_KEY[t])) || "Guide";
      setTab(firstTab);
      if (who.is_super || (who.tabs || []).includes("*") || (who.tabs || []).includes("inbox")) loadInbox(cred);
    } catch {
      setLoginErr("Invalid credentials or you don't have access.");
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    if (loginEmail.trim()) {
      // Email + password → exchange for a JWT, then authenticate with it.
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/auth/login`, { email: loginEmail.trim(), password: loginPassword });
        await authenticate(data.token);
      } catch (err) {
        const d = err?.response?.data?.detail;
        setLoginErr(typeof d === "string" ? d : "Invalid email or password.");
        setLoading(false);
      }
    } else {
      await authenticate(token);   // master admin token
    }
  };

  // Auto-login if a token is already stored (from a previous session).
  useEffect(() => {
    if (token && !loaded) authenticate(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Token": t } });
      setData(data);
    } catch {
      // non-inbox roles simply can't refresh inbox; not an error for them
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("ww_admin_token");
    setToken(""); setPrincipal(null); setLoaded(false);
    setLoginEmail(""); setLoginPassword("");
  };

  const [pastOn, setPastOn] = useState(true);
  useEffect(() => { if (settings) setPastOn(settings.show_past_roles !== false); }, [settings?.show_past_roles, settings]);
  const togglePastRoles = async () => {
    const next = !pastOn;
    setPastOn(next);
    try {
      await axios.post(`${API}/admin/positions/display-settings`, { show_past_roles: next }, { headers: { "X-Admin-Token": token } });
      toast.success(next ? "Past roles section is now visible" : "Past roles section hidden");
    } catch {
      setPastOn(!next);
      toast.error("Could not update the setting.");
    }
  };

  const updateTheme = async (patch) => {
    setSavingTheme(true);
    try {
      await save(token, patch);
      toast.success("Theme updated");
    } catch {
      toast.error("Could not save theme. Check admin token.");
    } finally {
      setSavingTheme(false);
    }
  };

  const resetTheme = () => updateTheme({
    bg_color: "#050505", text_color: "#F5F5F5", muted_color: "#8E8E93",
    heading_color: "#FFFFFF", accent_color: "#FF4500",
    secondary_color: "#DFFF00", tertiary_color: "#00E5FF",
    font_scale: 1.0, font_heading: "Cabinet Grotesk", font_body: "IBM Plex Sans",
  });

  if (!loaded) {
    return (
      <div data-testid="admin-login-page" className="px-6 md:px-14 py-16 md:py-24 min-h-[70vh] flex items-center">
        <form onSubmit={submitLogin} className="ww-card p-8 max-w-md w-full">
          <Lock size={22} color="#FF4500" weight="duotone" />
          <h1 className="mt-4 font-display text-3xl">Console access{roleSlug ? ` · ${roleSlug}` : ""}</h1>
          <p className="mt-2 text-[#8E8E93] text-sm">Sign in with your work email and password.</p>

          <label className="ww-label mt-6">Email</label>
          <input data-testid="admin-email-input" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="ww-input" placeholder="you@wisewit.ai" autoComplete="username" />
          <label className="ww-label mt-4">Password</label>
          <input data-testid="admin-password-input" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="ww-input" placeholder="••••••••" autoComplete="current-password" />

          <details className="mt-4">
            <summary className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93] cursor-pointer hover:text-white">Use master admin token</summary>
            <input data-testid="admin-token-input" type="password" value={token} onChange={(e) => { setToken(e.target.value); setLoginEmail(""); }} className="ww-input mt-3" placeholder="master token" />
          </details>

          {loginErr && <div data-testid="admin-login-error" className="mt-4 text-[#FF4500] text-sm">{loginErr}</div>}
          <button data-testid="admin-login-btn" disabled={loading} className="btn-primary mt-6 w-full justify-center">
            {loading ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const isLightBg = settings && /^#[0-9A-Fa-f]{6}$/.test(settings.bg_color) &&
    parseInt(settings.bg_color.slice(1, 3), 16) + parseInt(settings.bg_color.slice(3, 5), 16) + parseInt(settings.bg_color.slice(5, 7), 16) > 380;
  const textPresets = isLightBg ? TEXT_PRESETS_LIGHT_BG : TEXT_PRESETS_DARK_BG;

  return (
    <div data-testid="admin-page" className="px-6 md:px-14 py-16 md:py-24">
      <div className="flex items-center justify-between max-w-6xl">
        <div>
          <div className="text-overline mb-4">◢ Admin · CMS</div>
          <h1 className="font-display font-bold tracking-tighter text-4xl" style={{ color: "var(--ww-heading)" }}>Studio</h1>
          <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]" data-testid="admin-current-user">
            {principal?.name || principal?.email} · {principal?.role_slug}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-refresh" onClick={() => fetchData()} className="btn-secondary"><ArrowsClockwise size={14} /> Refresh</button>
          <button data-testid="admin-logout" onClick={logout} className="btn-ghost"><SignOut size={14} /> Sign out</button>
        </div>
      </div>

      <div className="mt-10 max-w-6xl flex flex-wrap gap-2 border-b border-[#2A2A2A] pb-px" data-testid="admin-tabs">
        {visibleTabs.map((t) => (
          <button
            key={t}
            data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setTab(t)}
            className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-3 border-b-2 -mb-px transition-colors ${
              tab === t ? "border-[#FF4500] text-white" : "border-transparent text-[#8E8E93] hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-10 max-w-6xl">
        {tab === "Inbox" && (
          <>
            <section>
              <div className="text-overline mb-4">◢ Project router submissions ({data.submissions.length})</div>
              <div className="border border-[#2A2A2A]">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Name</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-3">Challenge</div>
                </div>
                {data.submissions.length === 0 && <div className="px-4 py-10 text-[#555] text-sm text-center">No submissions yet.</div>}
                {data.submissions.map((s) => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-sm">
                    <div className="col-span-2 font-mono text-[11px] text-[#8E8E93]">{new Date(s.created_at).toLocaleString()}</div>
                    <div className="col-span-2 text-white">{s.name}</div>
                    <div className="col-span-3 text-[#DFFF00] font-mono text-xs">{s.email}</div>
                    <div className="col-span-2 text-[#FF4500] font-mono text-xs uppercase">{s.project_type}</div>
                    <div className="col-span-3 text-[#8E8E93] truncate" title={s.challenge}>{s.challenge}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16">
              <div className="text-overline mb-4">◢ Newsletter signups ({data.newsletter.length})</div>
              <div className="border border-[#2A2A2A]">
                <div className="grid grid-cols-3 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
                  <div>Date</div><div>Email</div><div>Source</div>
                </div>
                {data.newsletter.length === 0 && <div className="px-4 py-10 text-[#555] text-sm text-center">No signups yet.</div>}
                {data.newsletter.map((s) => (
                  <div key={s.id} className="grid grid-cols-3 gap-2 px-4 py-3 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-sm">
                    <div className="font-mono text-[11px] text-[#8E8E93]">{new Date(s.created_at).toLocaleString()}</div>
                    <div className="text-white">{s.email}</div>
                    <div className="text-[#8E8E93] font-mono text-xs">{s.source || '-'}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-16">
              <div className="text-overline mb-4">◢ Knowledge AI leads ({(data.knowledge_leads || []).length})</div>
              <div className="border border-[#2A2A2A]">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
                  <div className="col-span-2">Date</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-7">Question</div>
                </div>
                {(!data.knowledge_leads || data.knowledge_leads.length === 0) && <div className="px-4 py-10 text-[#555] text-sm text-center">No knowledge leads yet — these arrive when a visitor opts into the "email me this answer" card on /knowledge.</div>}
                {(data.knowledge_leads || []).map((s) => (
                  <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] text-sm">
                    <div className="col-span-2 font-mono text-[11px] text-[#8E8E93]">{new Date(s.created_at).toLocaleString()}</div>
                    <div className="col-span-3 text-[#DFFF00] font-mono text-xs truncate" title={s.email}>{s.email}</div>
                    <div className="col-span-7 text-white truncate" title={s.question}>{s.question}</div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {tab === "Guide"            && <GuidePanel setTab={setTab} />}
        {tab === "User access"      && <UserAccessPanel token={token} />}
        {tab === "Applications"     && <ApplicationsPanel token={token} />}
        {tab === "Live Vision"      && <CCTVPanel token={token} />}
        {tab === "Reels"            && (
          <>
            <ReelsResetBanner token={token} />
            <CMSPanel token={token} config={REEL_CONFIG} />
          </>
        )}
        {tab === "Case studies"     && <CMSPanel token={token} config={CASE_CONFIG} />}
        {tab === "Capabilities / Products" && <CMSPanel token={token} config={ATLAS_CONFIG} />}
        {tab === "Systems layers"   && <CMSPanel token={token} config={LAYERS_CONFIG} />}
        {tab === "Playbooks"        && <CMSPanel token={token} config={PLAYBOOKS_CONFIG} />}
        {tab === "Knowledge"        && <CMSPanel token={token} config={ARTICLE_CONFIG} />}
        {tab === "Careers" && (
          <>
            <div className="ww-card p-5 mb-4 flex items-center justify-between gap-4" data-testid="past-roles-toggle-card">
              <div>
                <div className="text-white font-display text-lg">Public &ldquo;Past roles&rdquo; section</div>
                <p className="text-[#8E8E93] text-sm mt-1 max-w-xl">Show archived roles at the bottom of the Careers page so candidates can see what you&apos;ve hired for. They can view but not apply.</p>
              </div>
              <button
                onClick={togglePastRoles}
                data-testid="past-roles-toggle"
                className={`font-mono text-[11px] tracking-[0.16em] uppercase px-4 py-2 border shrink-0 ${pastOn ? "border-[#00FFB2]/50 text-[#00FFB2]" : "border-[#8E8E93]/40 text-[#8E8E93]"}`}
              >
                {pastOn ? "Showing" : "Hidden"}
              </button>
            </div>
            <CMSPanel token={token} config={POSITION_CONFIG} />
          </>
        )}
        {tab === "Files"            && <FilesPanel token={token} />}
        {tab === "Analytics"        && <AnalyticsPanel token={token} />}
        {tab === "Deploy"           && <DeployPanel token={token} />}

        {tab === "Site appearance" && settings && (
          <div className="space-y-8" data-testid="theme-panel">
            {/* Background */}
            <div className="ww-card p-6 md:p-8">
              <div className="flex items-start gap-4">
                <Palette size={22} color="#DFFF00" weight="duotone" />
                <div className="flex-1">
                  <h3 className="font-display text-xl">Background color</h3>
                  <p className="mt-1 text-[#8E8E93] text-sm">Pick any color. Surfaces flip automatically when you pick a light shade.</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {BG_PRESETS.map((p) => {
                      const active = settings.bg_color?.toLowerCase() === p.value.toLowerCase();
                      return (
                        <button key={p.value} onClick={() => updateTheme({ bg_color: p.value })} data-testid={`preset-${p.value.replace('#','').toLowerCase()}`}
                          className={`flex items-center gap-2 border px-3 py-2 font-mono text-[11px] tracking-[0.12em] uppercase transition-colors ${active ? "border-[#FF4500]" : "border-[#2A2A2A] hover:border-[#404040]"}`}>
                          <span className="w-4 h-4 border border-[#2A2A2A]" style={{ background: p.value }} />
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4">
                    <label className="ww-label">Custom hex</label>
                    <input type="color" data-testid="theme-bg-picker" value={settings.bg_color || "#050505"} onChange={(e) => updateTheme({ bg_color: e.target.value })} className="w-16 h-10 bg-transparent border border-[#2A2A2A] cursor-pointer" />
                    <span className="ml-3 font-mono text-xs text-[#8E8E93]">{settings.bg_color}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Text colors */}
            <div className="ww-card p-6 md:p-8">
              <div className="flex items-start gap-4">
                <TextAa size={22} color="#DFFF00" weight="duotone" />
                <div className="flex-1">
                  <h3 className="font-display text-xl">Text colors</h3>
                  <p className="mt-1 text-[#8E8E93] text-sm">Body, muted, headings, and accent. We suggested a contrast-safe palette for your current background.</p>

                  <ColorRow label="Body text"     value={settings.text_color}    presets={textPresets} onChange={(v) => updateTheme({ text_color: v })}    testid="text-color" />
                  <ColorRow label="Muted text"    value={settings.muted_color}   presets={textPresets} onChange={(v) => updateTheme({ muted_color: v })}   testid="muted-color" />
                  <ColorRow label="Headings"      value={settings.heading_color} presets={textPresets} onChange={(v) => updateTheme({ heading_color: v })} testid="heading-color" />
                  <ColorRow label="Primary accent (CTAs)"          value={settings.accent_color}    presets={ACCENT_PRESETS} onChange={(v) => updateTheme({ accent_color: v })}    testid="accent-color" />
                  <ColorRow label="Secondary accent (highlights)"  value={settings.secondary_color} presets={ACCENT_PRESETS} onChange={(v) => updateTheme({ secondary_color: v })} testid="secondary-color" />
                  <ColorRow label="Tertiary accent (cyan slots)"   value={settings.tertiary_color}  presets={ACCENT_PRESETS} onChange={(v) => updateTheme({ tertiary_color: v })}  testid="tertiary-color" />
                </div>
              </div>
            </div>

            {/* Typography */}
            <div className="ww-card p-6 md:p-8">
              <div className="flex items-start gap-4">
                <TextT size={22} color="#DFFF00" weight="duotone" />
                <div className="flex-1">
                  <h3 className="font-display text-xl">Typography</h3>
                  <p className="mt-1 text-[#8E8E93] text-sm">Pick a heading + body font from a curated set. Adjust the scale to bump everything up or down.</p>

                  <div className="mt-6 grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="ww-label">Heading font</label>
                      <select data-testid="font-heading" value={settings.font_heading} onChange={(e) => updateTheme({ font_heading: e.target.value })} className="ww-input bg-[#050505]">
                        {HEADING_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <div className="mt-3 font-display text-3xl" style={{ fontFamily: `'${settings.font_heading}', sans-serif` }}>
                        We build the systems that run the floor.
                      </div>
                    </div>
                    <div>
                      <label className="ww-label">Body font</label>
                      <select data-testid="font-body" value={settings.font_body} onChange={(e) => updateTheme({ font_body: e.target.value })} className="ww-input bg-[#050505]">
                        {BODY_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <div className="mt-3 text-base text-[#D7D7D9]" style={{ fontFamily: `'${settings.font_body}', sans-serif` }}>
                        Computer vision, LLM copilots, voice interfaces, command-center dashboards — engineered to ship.
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <label className="ww-label">Font scale: {(settings.font_scale ?? 1).toFixed(2)}× ({Math.round((settings.font_scale ?? 1) * 16)}px base)</label>
                    <div className="flex items-center gap-4 mt-2">
                      <input
                        type="range" min="0.85" max="1.20" step="0.01"
                        value={settings.font_scale ?? 1}
                        onChange={(e) => updateTheme({ font_scale: Number(e.target.value) })}
                        data-testid="font-scale"
                        className="flex-1"
                      />
                      <button onClick={() => updateTheme({ font_scale: 1.0 })} className="btn-ghost">Reset 100%</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button data-testid="theme-reset-all" onClick={resetTheme} disabled={savingTheme} className="btn-secondary">Reset to Wisewit defaults</button>
              <div className="font-mono text-[10px] text-[#555]">{savingTheme && "saving…"}</div>
            </div>

            {/* Notification email */}
            <div className="ww-card p-6 md:p-8">
              <div className="flex items-start gap-4">
                <EnvelopeSimple size={22} color="#DFFF00" weight="duotone" />
                <div className="flex-1">
                  <h3 className="font-display text-xl">Notification email</h3>
                  <p className="mt-1 text-[#8E8E93] text-sm">
                    Where Wisewit sends Project Router submissions and Knowledge AI lead alerts. Change anytime.
                  </p>
                  <ContactEmailEditor settings={settings} updateTheme={updateTheme} savingTheme={savingTheme} />
                </div>
              </div>
            </div>

            {/* Digest email */}
            <div className="ww-card p-6 md:p-8">
              <DigestPanel settings={settings} updateTheme={updateTheme} savingTheme={savingTheme} token={token} />
            </div>

            {/* Footer products */}
            <div className="ww-card p-6 md:p-8">
              <FooterProductsEditor settings={settings} updateTheme={updateTheme} savingTheme={savingTheme} />
            </div>

            {/* Legal / policy documents */}
            <div className="ww-card p-6 md:p-8">
              <PolicyDocsEditor settings={settings} updateTheme={updateTheme} savingTheme={savingTheme} token={token} />
            </div>

            {/* KOSH copilot settings */}
            <div className="ww-card p-6 md:p-8">
              <KoshSettingsEditor settings={settings} updateTheme={updateTheme} savingTheme={savingTheme} />
            </div>

            {/* Theme snapshot: export/import */}
            <div className="ww-card p-6 md:p-8">
              <ThemeSnapshotEditor token={token} onImported={fetchData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ColorRow({ label, value, presets, onChange, testid }) {
  return (
    <div className="mt-6">
      <label className="ww-label">{label}</label>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <input type="color" data-testid={`${testid}-picker`} value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-12 h-9 bg-transparent border border-[#2A2A2A] cursor-pointer" />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="ww-input max-w-[160px]" placeholder="#FFFFFF" />
        {presets.map((p) => (
          <button key={p} onClick={() => onChange(p)} title={p}
            data-testid={`${testid}-preset-${p.replace('#','').toLowerCase()}`}
            className={`w-7 h-7 border ${value?.toLowerCase() === p.toLowerCase() ? "border-[#FF4500]" : "border-[#2A2A2A]"}`}
            style={{ background: p }} />
        ))}
      </div>
    </div>
  );
}

function ReelsResetBanner({ token }) {
  const [busy, setBusy] = useState(false);

  const purge = async () => {
    if (!window.confirm(
      "Delete ALL reels? This wipes every reel in the database and " +
      "prevents them being auto-re-seeded on restart. Cannot be undone."
    )) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/reels/purge-all`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Deleted ${data.deleted} reel${data.deleted === 1 ? "" : "s"}. Clean slate.`);
      // Force the CMSPanel to re-fetch by reloading the whole page — simplest,
      // preserves the current tab via localStorage / state defaults.
      setTimeout(() => window.location.reload(), 600);
    } catch {
      toast.error("Reset failed. Check the admin token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 px-4 py-3 border border-[#DFFF00]/40 bg-[#DFFF00]/[0.04] flex items-center justify-between gap-3 flex-wrap"
      data-testid="reels-reset-banner"
    >
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">
        ◢ Want a clean slate? Wipe all reels + prevent re-seed.
      </div>
      <button
        onClick={purge}
        disabled={busy}
        data-testid="reels-purge-all"
        className="btn-secondary text-[11px] py-2 disabled:opacity-50"
      >
        {busy ? "Wiping…" : "Reset all reels"}
      </button>
    </div>
  );
}

